import { Done, ErrorOutline, InfoOutlined } from "@mui/icons-material";
import { Box, Snackbar, Theme } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React from "react";
import { useIntl } from "react-intl";

import { LanguageKeys } from "../../language";

const styles = (theme: Theme) => ({
  "& .MuiSnackbarContent-root": {
    backgroundColor: theme.palette.common.white,
    border: `2px solid ${theme.palette.primary.main}`,
    color: theme.palette.common.black,
    fontSize: "1rem",
    boxShadow: 10,
  },
});

const messageStyles = () => ({
  "&": {
    display: "flex",
    alignItems: "center",
  },
  "& * + *": {
    marginLeft: "1rem",
  },
});

export interface MessageBarProps {
  isOpen: boolean;
  message: LanguageKeys | null;
  onClose: () => void;
  status?: "success" | "error" | "info";
}

export const MessageBar: React.FC<MessageBarProps> = ({
  isOpen,
  message,
  onClose,
  status = "success",
}) => {
  const { formatMessage } = useIntl();
  const messageValue = message
    ? formatMessage({ id: message as LanguageKeys })
    : "";

  const iconMap = {
    success: (
      <Done
        aria-hidden="true"
        sx={(theme: Theme) => ({
          color: theme.palette.primary.main,
        })}
      />
    ),
    error: (
      <ErrorOutline
        aria-hidden="true"
        sx={(theme: Theme) => ({
          color: theme.palette.primary.main,
        })}
      />
    ),
    info: (
      <InfoOutlined
        aria-hidden="true"
        sx={(theme: Theme) => ({
          color: theme.palette.primary.main,
        })}
      />
    ),
  };

  const hiddenMessageMap = {
    success: formatMessage({ id: "messageBar.hiddenSuccess" }),
    error: formatMessage({ id: "messageBar.hiddenFail" }),
    info: formatMessage({ id: "messageBar.hiddenInfo" }),
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={isOpen}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        onClose={() => {
          onClose();
        }}
        message={
          <Box sx={messageStyles}>
            {isOpen && iconMap[status]}
            <Box
              component="span"
              sx={visuallyHidden}
            >
              {hiddenMessageMap[status]}
            </Box>

            <Box component="span">{messageValue}</Box>
          </Box>
        }
        sx={styles}
      />
    </>
  );
};
